import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
    CalendarOutlined,
    ShareAltOutlined,
    SafetyOutlined,
    CarOutlined,
    TagsOutlined,
    EnvironmentOutlined,
    ToolOutlined,
    AppstoreAddOutlined,
    SafetyCertificateOutlined,
    FileProtectOutlined,
    PictureOutlined,
    UserAddOutlined,
    BankOutlined,
    GiftOutlined,
    PercentageOutlined,
    CreditCardOutlined,
    PhoneOutlined,
    CustomerServiceOutlined,
} from "@ant-design/icons";
import { useUser } from "../../../contexts/UserContext/UserContext";
import { useToast } from "../../../contexts/ToastContext";
import { markBookingSiteShared } from "./gettingStartedStorage";
import getBookingSiteUrl from "./bookingSiteUrl";
import ONBOARDING_HELP_LINKS from "../../onboarding/components/onboardingLinks";

const useActionGroups = () => {
    const { t } = useTranslation("dashboard");
    const history = useHistory();
    const { state, myCompanyData } = useUser();
    const toast = useToast();

    const handleCopyLink = async () => {
        const url = getBookingSiteUrl(myCompanyData?.id);
        if (!url) return;

        try {
            await navigator.clipboard.writeText(url);
            toast.success({ message: t("linkCopied") });
        } catch (_error) {
            /* clipboard may be unavailable, ignore */
        }

        markBookingSiteShared(state?.user?.id);
    };

    const handleCopyReferralCode = async () => {
        const code = myCompanyData?.referralCode;
        if (!code) return;

        try {
            await navigator.clipboard.writeText(code);
            toast.success({ message: t("referralCodeCopied") });
        } catch (_error) {
            /* clipboard may be unavailable, ignore */
        }
    };

    const goToAddVehicle = () => history.push("/cars?openModal=addCar");

    const actionGroups = [
        {
            key: "commonActions",
            label: t("commonActions"),
            items: [
                {
                    key: "viewAllReservations",
                    icon: <CalendarOutlined />,
                    label: t("viewAllReservations"),
                    description: t("viewAllReservationsDescription"),
                    onClick: () => history.push("/reservations"),
                },
                {
                    key: "copyWebsiteLink",
                    icon: <ShareAltOutlined />,
                    label: t("copyWebsiteLink"),
                    description: t("copyWebsiteLinkDescription"),
                    onClick: handleCopyLink,
                },
                {
                    key: "manageInsuranceProducts",
                    icon: <SafetyOutlined />,
                    label: t("manageInsuranceProducts"),
                    description: t("manageInsuranceProductsDescription"),
                    onClick: () => history.push("/insurance"),
                },
            ],
        },
        {
            key: "fleetAndVehicles",
            label: t("fleetAndVehicles"),
            items: [
                {
                    key: "addAVehicle",
                    icon: <CarOutlined />,
                    label: t("addAVehicle"),
                    description: t("addAVehicleDescription"),
                    onClick: goToAddVehicle,
                },
                {
                    key: "addCategories",
                    icon: <TagsOutlined />,
                    label: t("addCategories"),
                    description: t("addCategoriesDescription"),
                    onClick: () => history.push("/cars/categories?openModal=addCategory"),
                },
                {
                    key: "managePickupLocation",
                    icon: <EnvironmentOutlined />,
                    label: t("managePickupLocation"),
                    description: t("managePickupLocationDescription"),
                    onClick: () => history.push("/cars/locations"),
                },
                {
                    key: "scheduleMaintenance",
                    icon: <ToolOutlined />,
                    label: t("scheduleMaintenance"),
                    description: t("scheduleMaintenanceDescription"),
                    onClick: () => history.push("/cars"),
                },
                {
                    key: "createAddOn",
                    icon: <AppstoreAddOutlined />,
                    label: t("createAddOn"),
                    description: t("createAddOnDescription"),
                    onClick: () => history.push("/add-ons?openModal=createAddOn"),
                },
            ],
        },
        {
            key: "growthAndMarketing",
            label: t("growthAndMarketing"),
            items: [
                {
                    key: "previewBookingSite",
                    icon: <ShareAltOutlined />,
                    label: t("previewBookingSite"),
                    description: t("previewBookingSiteDescription"),
                    onClick: () => {
                        const url = getBookingSiteUrl(myCompanyData?.id);
                        if (url) window.open(url, "_blank", "noopener,noreferrer");
                    },
                },
            ],
        },
        {
            key: "insuranceAndCoverage",
            label: t("insuranceAndCoverage"),
            items: [
                {
                    key: "requestCoverage",
                    icon: <SafetyCertificateOutlined />,
                    label: t("requestCoverage"),
                    description: t("requestCoverageDescription"),
                    onClick: () => history.push("/settings?menu=integration&tab=insurance"),
                },
                {
                    key: "newPolicySale",
                    icon: <FileProtectOutlined />,
                    label: t("newPolicySale"),
                    description: t("newPolicySaleDescription"),
                    onClick: () => history.push("/sell-insurance?openModal=newPolicySale"),
                },
            ],
        },
        {
            key: "accountAndTeam",
            label: t("accountAndTeam"),
            items: [
                {
                    key: "uploadCompanyLogo",
                    icon: <PictureOutlined />,
                    label: t("uploadCompanyLogo"),
                    description: t("uploadCompanyLogoDescription"),
                    onClick: () => history.push("/settings?menu=companyInfo"),
                },
                {
                    key: "addEmployee",
                    icon: <UserAddOutlined />,
                    label: t("addEmployee"),
                    description: t("addEmployeeDescription"),
                    onClick: () => history.push("/settings/employees/new"),
                },
                {
                    key: "companyDetails",
                    icon: <BankOutlined />,
                    label: t("companyDetails"),
                    description: t("companyDetailsDescription"),
                    onClick: () => history.push("/settings?menu=companyInfo"),
                },
                {
                    key: "copyReferralCode",
                    icon: <GiftOutlined />,
                    label: t("copyReferralCode"),
                    description: t("copyReferralCodeDescription"),
                    onClick: handleCopyReferralCode,
                },
                {
                    key: "manageTaxInformation",
                    icon: <PercentageOutlined />,
                    label: t("manageTaxInformation"),
                    description: t("manageTaxInformationDescription"),
                    onClick: () => history.push("/settings?menu=taxInfo"),
                },
                {
                    key: "paymentAndInsuranceIntegration",
                    icon: <CreditCardOutlined />,
                    label: t("paymentAndInsuranceIntegration"),
                    description: t("paymentAndInsuranceIntegrationDescription"),
                    onClick: () => history.push("/settings?menu=integration"),
                },
            ],
        },
        {
            key: "supportGroup",
            label: t("supportGroup"),
            items: [
                {
                    key: "contactSupport",
                    icon: <CustomerServiceOutlined />,
                    label: t("contactSupport"),
                    description: t("contactSupportDescription"),
                    onClick: () => window.open(ONBOARDING_HELP_LINKS.whatsApp, "_blank", "noopener,noreferrer"),
                },
                {
                    key: "scheduleACall",
                    icon: <PhoneOutlined />,
                    label: t("scheduleACall"),
                    description: t("scheduleACallDescription"),
                    onClick: () => window.open(ONBOARDING_HELP_LINKS.bookDemo, "_blank", "noopener,noreferrer"),
                },
            ],
        },
    ];

    return {
        actionGroups,
        goToAddVehicle,
        handleCopyLink,
    };
};

export default useActionGroups;
